/**
 * Drop-in ESM replacement for `use-sync-external-store/shim/with-selector.js`
 * (aliased to this file in vite.config.ts). @tanstack/react-router pulls this
 * in via @tanstack/react-store; the upstream file is a CJS module that does
 * `require("react")` + `require("use-sync-external-store/shim")` at the top
 * level. @module-federation/vite's shared-import rewrite for `react` mishandles
 * that nested CJS require chain and crashes with `ReferenceError: $x is not
 * defined` in the remote's onLoad (the generated fallback bundle references
 * React through a binding it never actually declares).
 *
 * React 18+ ships `useSyncExternalStore` natively, so the "shim" indirection
 * this package exists for is unnecessary here — this reimplements
 * `useSyncExternalStoreWithSelector` directly against React's own hook via a
 * plain ESM `import`, which the shared-react rewrite handles correctly.
 * Algorithm ported verbatim from upstream
 * `use-sync-external-store/cjs/use-sync-external-store-shim/with-selector.production.js`
 * (React, MIT licensed) — keep in sync if that package's logic changes.
 */
import { useDebugValue, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';

function isEq(x: unknown, y: unknown): boolean {
  // biome-ignore lint/suspicious/noSelfCompare: NaN checks (x !== x, y !== y) are the Object.is polyfill itself
  return (x === y && (x !== 0 || 1 / (x as number) === 1 / (y as number))) || (x !== x && y !== y);
}

const objectIs: (x: unknown, y: unknown) => boolean =
  typeof Object.is === 'function' ? Object.is : isEq;

interface Inst<Selection> {
  hasValue: boolean;
  value: Selection | null;
}

export function useSyncExternalStoreWithSelector<Snapshot, Selection>(
  subscribe: (onStoreChange: () => void) => () => void,
  getSnapshot: () => Snapshot,
  getServerSnapshot: (() => Snapshot) | undefined,
  selector: (snapshot: Snapshot) => Selection,
  isEqual?: (a: Selection, b: Selection) => boolean
): Selection {
  const instRef = useRef<Inst<Selection> | null>(null);
  let inst: Inst<Selection>;
  if (instRef.current === null) {
    inst = { hasValue: false, value: null };
    instRef.current = inst;
  } else {
    inst = instRef.current;
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: inst is a stable ref, read here only for the first memoized selection; excluding it matches upstream's algorithm
  const [memoizedGetSnapshot, memoizedGetServerSnapshot] = useMemo(() => {
    let hasMemo = false;
    let memoizedSnapshot: Snapshot;
    let memoizedSelection: Selection;

    const memoizedSelector = (nextSnapshot: Snapshot): Selection => {
      if (!hasMemo) {
        hasMemo = true;
        memoizedSnapshot = nextSnapshot;
        const nextSelection = selector(nextSnapshot);
        if (isEqual !== undefined && inst.hasValue) {
          const currentSelection = inst.value as Selection;
          if (isEqual(currentSelection, nextSelection)) {
            memoizedSelection = currentSelection;
            return memoizedSelection;
          }
        }
        memoizedSelection = nextSelection;
        return memoizedSelection;
      }

      const currentSelection = memoizedSelection;
      if (objectIs(memoizedSnapshot, nextSnapshot)) return currentSelection;

      const nextSelection = selector(nextSnapshot);
      if (isEqual?.(currentSelection, nextSelection)) {
        memoizedSnapshot = nextSnapshot;
        return currentSelection;
      }
      memoizedSnapshot = nextSnapshot;
      memoizedSelection = nextSelection;
      return memoizedSelection;
    };

    const maybeGetServerSnapshot = getServerSnapshot === undefined ? null : getServerSnapshot;
    return [
      () => memoizedSelector(getSnapshot()),
      maybeGetServerSnapshot === null
        ? undefined
        : () => memoizedSelector(maybeGetServerSnapshot()),
    ] as const;
  }, [getSnapshot, getServerSnapshot, selector, isEqual]);

  const value = useSyncExternalStore(subscribe, memoizedGetSnapshot, memoizedGetServerSnapshot);

  useEffect(() => {
    inst.hasValue = true;
    inst.value = value;
  }, [inst, value]);

  useDebugValue(value);
  return value;
}
